// src/pages/dashboards/DashboardViewPage.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Spinner } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ArrowLeft, Edit2, RefreshCw, Clock, Star, Share2 } from 'lucide-react';
import { useDashboard } from '@/hooks/queries/useReportes';
import { PeriodoReporte } from '@/types/reporte';
import { WidgetRenderer } from './components/WidgetRenderer';

const PERIODO_LABELS: Record<PeriodoReporte, string> = {
  [PeriodoReporte.HOY]: 'Hoy',
  [PeriodoReporte.AYER]: 'Ayer',
  [PeriodoReporte.SEMANA]: 'Esta semana',
  [PeriodoReporte.SEMANA_PASADA]: 'Semana pasada',
  [PeriodoReporte.MES]: 'Este mes',
  [PeriodoReporte.MES_PASADO]: 'Mes pasado',
  [PeriodoReporte.TRIMESTRE]: 'Este trimestre',
  [PeriodoReporte.ANO]: 'Este ano',
  [PeriodoReporte.PERSONALIZADO]: 'Personalizado',
};

export function DashboardViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState<PeriodoReporte>(PeriodoReporte.MES);
  const { data: dashboard, isLoading, refetch } = useDashboard(id!, periodo);

  // Auto-refresh si esta configurado
  useEffect(() => {
    if (!dashboard?.auto_refresh_segundos) return;

    const interval = setInterval(() => {
      refetch();
    }, dashboard.auto_refresh_segundos * 1000);

    return () => clearInterval(interval);
  }, [dashboard?.auto_refresh_segundos, refetch]);

  // Establecer periodo por defecto del dashboard
  useEffect(() => {
    if (dashboard?.periodo_default) {
      setPeriodo(dashboard.periodo_default);
    }
  }, [dashboard?.periodo_default]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Dashboard no encontrado</p>
        <Button variant="ghost" onClick={() => navigate('/dashboards')} className="mt-4">
          Volver a dashboards
        </Button>
      </div>
    );
  }

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboards')}
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{dashboard.nombre}</h1>
              {dashboard.es_default && (
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              )}
              {dashboard.es_compartido && (
                <Badge className="flex items-center gap-1">
                  <Share2 className="h-3 w-3" />
                  Compartido
                </Badge>
              )}
            </div>
            {dashboard.descripcion && (
              <p className="text-gray-500 mt-1">{dashboard.descripcion}</p>
            )}
            {dashboard.auto_refresh_segundos && (
              <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                <Clock className="h-4 w-4" />
                <span>Auto-refresh cada {dashboard.auto_refresh_segundos}s</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={periodo}
            onValueChange={(value) => setPeriodo(value as PeriodoReporte)}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PERIODO_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate(`/dashboards/${id}/editar`)}>
            <Edit2 className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      {/* Widgets Grid */}
      {dashboard.widgets.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              Este dashboard no tiene widgets. Agrega métricas para comenzar.
            </p>
            <Button onClick={() => navigate(`/dashboards/${id}/editar`)}>
              <Edit2 className="h-4 w-4 mr-2" />
              Agregar widgets
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {dashboard.widgets
            .filter((w) => w.visible)
            .sort((a, b) => a.orden - b.orden)
            .map((widget) => (
              <WidgetRenderer key={widget.id} widget={widget} />
            ))}
        </div>
      )}
    </div>
  );
}
